<div class="settings-page branches-page">
  <div class="settings-topbar">
    <div class="topbar-left">
      <h1>
        Branches
        <small>Manage company branches</small>
      </h1>
      <div class="breadcrumb">
        <i class="bi bi-house-door"></i>
        Settings / <span class="current">Branches</span>
      </div>
    </div>
    <div class="topbar-right">
      <button type="button" class="btn btn-primary" (click)="openModal()">
        <i class="bi bi-plus-circle"></i> Add Branch
      </button>
    </div>
  </div>

  <div class="settings-table-wrap">
    <div class="table-scroll">
      <table class="report-data-table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Name</th>
            <th>City</th>
            <th>Status</th>
            <th class="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (row of branches(); track row.id) {
            <tr>
              <td><span class="fw-semibold">{{ row.code }}</span></td>
              <td>{{ row.name }}</td>
              <td>{{ row.city || '—' }}</td>
              <td>
                <span class="status-pill" [class.active]="row.status === 'active'">
                  {{ row.status }}
                </span>
              </td>
              <td class="actions-cell">
                <div class="row-actions">
                  <button type="button" class="btn-action edit" title="Edit branch" (click)="openEdit(row)">
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button type="button" class="btn-action delete" title="Delete branch" (click)="deleteRow(row)">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          } @empty {
            <tr>
              <td colspan="5" class="text-center py-4 text-muted">
                <i class="bi bi-inbox empty-state-icon"></i>
                No branches found. Click "Add Branch" to create one.
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </div>
</div>

@if (showModal()) {
  <div class="modal-overlay open" (click)="closeModal()">
    <div class="settings-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2 class="modal-title">
          <i class="bi" [class]="isEditing() ? 'bi-pencil' : 'bi-plus-circle'"></i>
          {{ isEditing() ? 'Edit Branch' : 'Add Branch' }}
        </h2>
        <button type="button" class="close-btn" (click)="closeModal()">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="modal-body">
        <div class="form-group">
          <label class="form-label">Code</label>
          @if (isEditing() && branchForm.code) {
            <input type="text" class="form-control" [value]="branchForm.code" disabled />
            <small class="text-muted">Generated automatically and cannot be changed.</small>
          } @else {
            <input type="text" class="form-control" value="Auto-generated on save" disabled />
            <small class="text-muted">A unique code is created when you save.</small>
          }
        </div>
        <div class="form-group">
          <label class="form-label">Name <span class="text-danger">*</span></label>
          <input type="text" class="form-control" [(ngModel)]="branchForm.name" placeholder="Enter branch name" />
        </div>
        <div class="form-group">
          <label class="form-label">City</label>
          <input type="text" class="form-control" [(ngModel)]="branchForm.city" placeholder="Enter city" />
        </div>
        <div class="form-group">
          <label class="form-label">Status</label>
          <select class="form-select" [(ngModel)]="branchForm.status">
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeModal()">
          <i class="bi bi-x-lg"></i> Cancel
        </button>
        <button type="button" class="btn btn-primary" (click)="save()">
          <i class="bi bi-check-lg"></i> {{ isEditing() ? 'Save Changes' : 'Save' }}
        </button>
      </div>
    </div>
  </div>
}

@if (showDeleteModal()) {
  <div class="modal-overlay open" (click)="closeDeleteModal()">
    <div class="settings-modal delete-impact-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2 class="modal-title">
          <i class="bi bi-trash"></i>
          Delete Branch
        </h2>
        <button type="button" class="close-btn" (click)="closeDeleteModal()">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="modal-body">
        @if (deleteLoading()) {
          <p class="text-muted mb-0">Checking for stock, items, and users…</p>
        } @else {
          @if (deleteImpact(); as impact) {
            <p class="delete-lead">
              Delete <strong>{{ deleteTarget()?.name }}</strong>?
            </p>

            @if (impact.isHomeBranch) {
              <div class="impact-banner">
                <i class="bi bi-exclamation-triangle-fill"></i>
                <div>
                  <strong>Home branch</strong>
                  <span>You cannot delete the branch you are signed into.</span>
                </div>
              </div>
            } @else if (impact.userCount > 0) {
              <div class="impact-banner">
                <i class="bi bi-exclamation-triangle-fill"></i>
                <div>
                  <strong>{{ impact.userCount }} user(s) still assigned</strong>
                  <span>Reassign those users to another branch before deleting.</span>
                </div>
              </div>
            } @else if (impact.requiresReassignment) {
              <div class="impact-banner">
                <i class="bi bi-exclamation-triangle-fill"></i>
                <div>
                  <strong>This branch still has items.</strong>
                  <span>
                    {{ impact.distinctItemCount }} stock item(s)
                    ({{ impact.totalOnHand }} on hand)
                    @if (impact.assetCount > 0) {
                      · {{ impact.assetCount }} item(s)
                    }
                    across {{ impact.locationCount }} location(s) must be moved.
                  </span>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">
                  Move items to branch <span class="text-danger">*</span>
                </label>
                <select
                  class="form-select"
                  [ngModel]="targetBranchId()"
                  (ngModelChange)="targetBranchId.set($event); onTargetBranchChange()"
                >
                  <option value="">Select destination branch</option>
                  @for (branch of destinationBranches(); track branch.id) {
                    <option [value]="branch.id">{{ branch.name }}</option>
                  }
                </select>
              </div>

              <div class="form-group">
                <label class="form-label">
                  Destination store location <span class="text-danger">*</span>
                </label>
                <select
                  class="form-select"
                  [ngModel]="targetStoreLocationId()"
                  (ngModelChange)="targetStoreLocationId.set($event)"
                  [disabled]="!targetBranchId()"
                >
                  <option value="">Select store location</option>
                  @for (loc of destinationLocations(); track loc.id) {
                    <option [value]="loc.id">{{ loc.name }}</option>
                  }
                </select>
                @if (targetBranchId() && !destinationLocations().length) {
                  <small class="text-danger">
                    That branch has no active store locations.
                  </small>
                }
              </div>

              @if (impact.items.length) {
                <div class="impact-items">
                  <div class="impact-items-title">Items to move</div>
                  <ul>
                    @for (item of impact.items.slice(0, 8); track $index) {
                      <li>
                        <span>
                          {{ item.itemName }}
                          @if (item.locationName) {
                            <small>({{ item.locationName }})</small>
                          }
                        </span>
                        <span class="qty">{{ item.quantityOnHand }}</span>
                      </li>
                    }
                    @if (impact.items.length > 8) {
                      <li class="more">+{{ impact.items.length - 8 }} more…</li>
                    }
                  </ul>
                </div>
              }
            } @else {
              <p class="text-muted mb-0">
                No stock or items are in this branch. It can be deleted safely
                @if (impact.locationCount > 0) {
                  ({{ impact.locationCount }} empty location(s) will also be deactivated)
                }.
              </p>
            }
          }
        }

        @if (deleteError()) {
          <div class="delete-error">{{ deleteError() }}</div>
        }
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeDeleteModal()" [disabled]="deleteSubmitting()">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-danger"
          (click)="confirmDelete()"
          [disabled]="
            deleteLoading() ||
            deleteSubmitting() ||
            !deleteImpact() ||
            deleteImpact()!.isHomeBranch ||
            deleteImpact()!.userCount > 0 ||
            (deleteImpact()!.requiresReassignment && (!targetBranchId() || !targetStoreLocationId() || !destinationLocations().length))
          "
        >
          <i class="bi bi-trash"></i>
          {{ deleteSubmitting() ? 'Deleting…' : 'Delete Branch' }}
        </button>
      </div>
    </div>
  </div>
}

@if (showToast()) {
  <div class="toast-container show">
    <div class="toast" [class.success]="!toastIsError()" [class.error]="toastIsError()">
      <div class="toast-icon">
        <i class="bi" [class]="toastIsError() ? 'bi-exclamation-circle-fill' : 'bi-check-circle-fill'"></i>
      </div>
      <div class="toast-content">
        <div class="toast-title">{{ toastIsError() ? 'Unable to save' : 'Success!' }}</div>
        <div class="toast-message">{{ toastMessage() }}</div>
      </div>
    </div>
  </div>
}
